import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AppNavbar } from '../components/AppNavbar';
import { toast } from 'sonner';
import { CreditCard, Save, Check, ArrowRight, Sparkles, Zap, Crown, Building2 } from 'lucide-react';

const PLAN_META = {
  free:    { label: 'Free',       Icon: Sparkles, color: '#6B7280', tagline: 'Get started — no credit card' },
  starter: { label: 'Starter',    Icon: Zap,      color: '#3B82F6', tagline: 'Everything you need to start' },
  pro:     { label: 'Pro',        Icon: Crown,    color: '#8B5CF6', tagline: 'Unlimited everything — go all in' },
  team:    { label: 'Team / Broker', Icon: Building2, color: '#10B981', tagline: 'Power your entire brokerage' },
};

const UsageRow = ({ label, used, limit }) => {
  const unlimited = limit === -1;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / Math.max(1, limit)) * 100));
  const atLimit = !unlimited && used >= limit;
  const nearLimit = !unlimited && pct >= 80;
  const barColor = atLimit ? '#EF4444' : nearLimit ? '#F59E0B' : '#10B981';
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0" data-testid={`usage-row-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <span className="text-sm text-gray-600" style={{ fontFamily: 'Manrope, sans-serif' }}>{label}</span>
      <div className="flex items-center gap-3">
        <span className="text-sm font-semibold tabular-nums" style={{ fontFamily: 'Outfit, sans-serif', color: atLimit ? '#EF4444' : '#111' }}>
          {used} / {unlimited ? <span className="text-emerald-500">∞</span> : limit}
        </span>
        {!unlimited && (
          <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: barColor }} />
          </div>
        )}
      </div>
    </div>
  );
};

export const AccountSettingsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Plan + usage (live from /api/plan/current)
  const [planType, setPlanType] = useState('free');
  const [usage, setUsage] = useState(null);

  // Billing details
  const [billingEmail, setBillingEmail] = useState('');
  const [paymentMethod, setPaymentMethod] = useState(null);

  useEffect(() => {
    Promise.all([fetchPlan(), fetchBilling()]).finally(() => setLoading(false));
  }, []);

  const fetchPlan = async () => {
    try {
      const res = await apiClient.get('/plan/current');
      setPlanType(res.data.plan || 'free');
      setUsage(res.data.usage || null);
    } catch {
      // Fall back silently
    }
  };

  const fetchBilling = async () => {
    try {
      const res = await apiClient.get('/profile/billing');
      setBillingEmail(res.data.billing_email || user?.email || '');
      setPaymentMethod(res.data.payment_method);
    } catch {
      setBillingEmail(user?.email || '');
    }
  };

  const saveBilling = async () => {
    setSaving(true);
    try {
      await apiClient.put('/profile/billing', { billing_email: billingEmail });
      toast.success('Billing details updated');
    } catch {
      toast.error('Failed to update billing details');
    } finally {
      setSaving(false);
    }
  };

  const isPaidPlan = planType !== 'free';
  const meta = PLAN_META[planType] || PLAN_META.free;
  const PlanIcon = meta.Icon;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading account settings...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="noise-texture" />

      <AppNavbar />

      {/* Content */}
      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <h2 className="text-3xl font-bold mb-8" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="settings-title">Account Settings</h2>

        {/* Subscription / Plan Section */}
        <div className="border border-gray-200 rounded-2xl p-6 bg-white space-y-5 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${meta.color}15` }} data-testid="plan-icon-wrap">
                <PlanIcon className="h-6 w-6" style={{ color: meta.color }} />
              </div>
              <div>
                <p className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="current-plan-name">
                  {meta.label} Plan
                </p>
                <p className="text-sm text-gray-500" style={{ fontFamily: 'Manrope, sans-serif' }}>{meta.tagline}</p>
              </div>
            </div>
            {isPaidPlan ? (
              <Button
                variant="outline"
                onClick={() => navigate('/subscription')}
                data-testid="manage-plan-button"
                className="gap-1.5"
              >
                Manage Plan <ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/subscription')}
                data-testid="upgrade-plan-button"
                className="bg-black text-white hover:bg-gray-900 gap-1.5"
              >
                Upgrade Plan <ArrowRight className="h-4 w-4" />
              </Button>
            )}
          </div>

          {usage && (
            <div className="pt-2 border-t border-gray-100">
              <p className="text-xs uppercase tracking-wider font-semibold text-gray-400 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                This Month's Usage
              </p>
              <UsageRow label="Posts" used={usage.posts_used} limit={usage.posts_limit} />
              <UsageRow label="Active Listings" used={usage.listings_used} limit={usage.listings_limit} />
              <UsageRow label="Magic Scrapes" used={usage.scrapes_used} limit={usage.scrapes_limit} />
              <UsageRow label="Scheduled Posts" used={usage.scheduled_used} limit={usage.scheduled_limit} />
            </div>
          )}

          {isPaidPlan && (
            <div className="p-3 border border-emerald-200 bg-emerald-50 rounded-lg flex items-center gap-2" data-testid="active-subscription-banner">
              <Check className="h-4 w-4 text-emerald-500" />
              <span className="text-sm font-medium text-emerald-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Active subscription — billed via PayPal
              </span>
            </div>
          )}
        </div>

        {/* Payment Method Section */}
        <div className="border border-border/50 rounded-lg p-6 bg-card space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-4">Payment Method</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="billingEmail">Billing Email</Label>
                <Input
                  id="billingEmail"
                  value={billingEmail}
                  onChange={(e) => setBillingEmail(e.target.value)}
                  className="mt-1"
                  data-testid="billing-email-input"
                />
              </div>
              {paymentMethod ? (
                <div className="p-4 border border-border/50 rounded-lg">
                  <p className="text-sm font-medium">{paymentMethod}</p>
                  <Button variant="outline" size="sm" className="mt-2" data-testid="update-payment-button">
                    Update Payment Method
                  </Button>
                </div>
              ) : (
                <Button variant="outline" data-testid="add-payment-button">
                  <CreditCard className="h-4 w-4 mr-2" />
                  Add Payment Method
                </Button>
              )}
            </div>
          </div>

          <Button onClick={saveBilling} disabled={saving} data-testid="save-billing-button">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>

        {/* View All Plans CTA */}
        <div className="mt-8 p-6 border border-gray-200 rounded-2xl bg-gradient-to-br from-gray-50 to-white flex items-center justify-between" data-testid="view-all-plans-card">
          <div>
            <h3 className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Explore all plans
            </h3>
            <p className="text-sm text-gray-500 mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Compare Free, Starter, Pro, and Team plans side-by-side.
            </p>
          </div>
          <Button
            onClick={() => navigate('/subscription')}
            className="bg-black text-white hover:bg-gray-900 gap-1.5"
            data-testid="view-all-plans-btn"
          >
            View Plans <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </main>
    </div>
  );
};